import { Menu, MenuItem, Position, Text } from "@blueprintjs/core"
import { Popover2 } from "@blueprintjs/popover2"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomTag from "components/Atoms/CustomTag"
import CustomTooltip from "components/Atoms/CustomTooltip"
import CustomOverflowList from "components/Molecules/CustomOverflowList"
import DataCell, { Subtitle, Title } from "components/Organisms/table/DataCell"
import CustomReactTable from "components/Templates/CustomReactTable"
import dayjs from "dayjs"
import { TransactionStatus } from "generated/graphql"
import { useAllTransactions } from "graphql/fund-flow/fund-flow.hooks"
import React, { useState } from "react"
import clsx from "utils/clsx"
import { DEFAULT_LOCALE } from "utils/formatters.utils"
import EditReconcileStatus from "./EditReconcileStatus"
import EditTransactionStatus from "./EditTransactionStatus"

const TransactionList = () => {
  const { data, loading } = useAllTransactions()

  const COLUMNS = [
    {
      width: 220,
      Header: "DC Name",
      accessor: "account.distributioncentreSet[0].name",
      Cell({ value }) {
        return value || "---"
      },
    },
    {
      Header: "Payment Method",
      accessor: "paymentMethod.name",
      Cell({ value, original }) {
        return (
          <DataCell>
            <Title>{value}</Title>
            <Subtitle>{dayjs(original.date).format("Do MMM YYYY")}</Subtitle>
          </DataCell>
        )
      },
    },
    {
      width: 360,
      Header: "Reference ID",
      accessor: "referenceId",
      Cell({ value }) {
        return <div>{value}</div>
      },
    },
    {
      width: 150,
      Header: "Fund Deposited",
      accessor: "amount",
      Cell({ value, original }) {
        return (
          <span className="font-500">
            {value.toLocaleString(DEFAULT_LOCALE, {
              style: "currency",
              currency: original.currency.name,
              maximumFractionDigits: 2,
            })}
          </span>
        )
      },
    },
    {
      Header: "Attachments",
      accessor: "attachments",
      Cell({ value, original }) {
        const [preview, setPreview] = useState(null)

        return (
          <div className="vt-text-left px-3">
            <div>
              <span
                style={{
                  cursor: "pointer",
                  fontStyle: "italic",
                  textDecoration: "underline",
                  color: "#2B60AF",
                }}
              >
                Attachment
              </span>
              <CustomOverflowList
                items={value?.filter(p => p?.url) ?? []}
                overflowRenderer={value => {
                  return (
                    <Popover2
                      minimal
                      position={Position.BOTTOM_RIGHT}
                      key={value?.length}
                      content={
                        <Menu className="vt-overflow-list-menu">
                          {value?.map((photo: any) => {
                            return (
                              <MenuItem
                                onClick={e => setPreview(photo)}
                                icon={
                                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                  <img
                                    alt={photo?.url}
                                    key={photo?.id}
                                    src={photo?.url}
                                    width="25px"
                                    height="25px"
                                    className="vt-horizontal-stack"
                                  />
                                }
                                key={photo?.id}
                                text={dayjs(photo?.createdAt).format(
                                  "Do MMMM YYYY, h:mm A"
                                )}
                              />
                            )
                          })}
                        </Menu>
                      }
                    >
                      <CustomTag
                        key={"vt-total-tag"}
                        className="ml-3"
                        interactive
                        round
                        large
                        intent="primary"
                        minimal
                      >
                        + {value?.length}
                      </CustomTag>
                    </Popover2>
                  )
                }}
                visibleItemRenderer={(item: any) => {
                  return (
                    <CustomTooltip
                      key={item?.id}
                      light
                      content={
                        <img
                          alt={item?.id}
                          key={item?.id}
                          src={item?.url}
                          className="h-72 w-auto"
                        />
                      }
                    >
                      <img
                        alt={item?.id}
                        onClick={e => setPreview(item)}
                        key={item?.id}
                        src={item?.url}
                        className={clsx(
                          "vt-horizontal-stack",
                          "w-8 h-8 object-cover"
                        )}
                      />
                    </CustomTooltip>
                  )
                }}
              />
              <CustomDialog
                width="50%"
                style={{
                  height: "80vh",
                }}
                scrollable={false}
                isOpen={preview?.url ? true : false}
                onClose={e => setPreview(null)}
                title="Attachment"
                light
              >
                <div className="flex flex-row gap-4">
                  <div className="border-2 border-dashed bg-white p-2 w-1/2">
                    <img
                      alt={preview?.id}
                      className="max-w-full h-auto"
                      src={preview?.url}
                    />
                  </div>
                  <div className="w-1/2">
                    <table
                      style={{
                        borderSpacing: "0 1em",
                      }}
                      className="w-full border-separate"
                    >
                      <tr>
                        <td className="font-500 ">Cash :</td>
                        <td className="flex justify-end">
                          <CustomTag large className="text-right">
                            {original.amount?.toLocaleString?.(DEFAULT_LOCALE, {
                              style: "currency",
                              currency: original.currency.name,
                              maximumFractionDigits: 2,
                            })}
                          </CustomTag>
                        </td>
                      </tr>
                      <tr>
                        <td className="font-500">Date :</td>
                        <td className="text-right">{original.date}</td>
                      </tr>
                      <tr>
                        <td className="font-500 whitespace-nowrap">
                          Reference ID :
                        </td>
                        <td className="text-right">{original.referenceId}</td>
                      </tr>
                      <tr>
                        <td className="font-500">Remark :</td>
                        <td className="text-right">
                          {original.remark
                            ? original.remark
                            : "Entry Should be done"}
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </CustomDialog>
            </div>
          </div>
        )
      },
    },
    {
      Header: "Remark",
      accessor: "remark",
      Cell({ value }) {
        return <Text ellipsize>{value}</Text>
      },
    },
    {
      width: 200,
      Header: "Status",
      accessor: "status",

      Cell({ value, original }) {
        return (
          <EditTransactionStatus uuid={original.transactionId} status={value} />
        )
      },
    },
    {
      width: 200,
      Header: "Reconcile Status",
      accessor: "status",

      Cell({ value, original }) {
        return (
          <EditReconcileStatus uuid={original.transactionId} status={value} />
        )
      },
    },
  ]
  return (
    <CustomReactTable
      loading={loading}
      loadingText="Fetching data"
      data={data?.allTransactions ?? []}
      columns={COLUMNS}
      getTrProps={(_, rowInfo) => {
        const status = rowInfo?.original?.status

        return {
          className:
            status === TransactionStatus.FAL
              ? "bg-yellow-50"
              : status === TransactionStatus.RCF
              ? "bg-red-50"
              : status === TransactionStatus.RCN
              ? "bg-green-50"
              : null,
        }
      }}
    />
  )
}

export default TransactionList
